import 'antd/dist/reset.css';
import './i18n.config';

import { Skeleton } from 'antd';
import { AccessGuardian } from 'common/components';
import { ROUTES } from 'common/constants';
import { ActionRbacEnum } from 'common/utils/auth-provider.util';
import { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import EntitlementEnum from './pages/organization/entitlements.enum';
import { RequireOrganization } from './pages/organization/require-organization.component';
import {
  AccountsPayableContainer,
  AgOperationContainer,
  AgOperationCreateContainer,
  AuthContainer,
  BalanceAiListContainer,
  BalanceAiTermsAndConditions,
  BankAccountListContainer,
  CommercializationContractsContainer,
  CommercializationMovementsContainer,
  CommercializationStockContainer,
  DashboardContainer,
  DataImportDetailsContainer,
  DataImportListContainer,
  DataImportStartContainer,
  ElectronicInvoiceContainer,
  ElectronicInvoiceLinkContainer,
  FinancialBudgetContainer,
  FinancialContainer,
  FinancialMachineryContainer,
  FinancialProductionContainer,
  HelpCenterContainer,
  HomeContainer,
  ImportContainer,
  InvoiceListContainer,
  ItemCategoryContainer,
  ItemListContainer,
  MachineryListContainer,
  MaintenanceContainer,
  MaterialConsumptionListContainer,
  MigrationContainer,
  MyCropwiseContainer,
  NewMovementsContainer,
  NewStockSummaryContainer,
  NoCostRecordsContainer,
  NoOrganization,
  NoPermission,
  NoPermissionBalance,
  PurchaseListContainer,
  ReportGeneralLedger,
  ReportsContainer,
  SeasonBudgetContainer,
  SettingsContainer,
  SupplierList,
} from './routes-balance.lazy';
import {
  StructureTemplate,
  StructureTemplateMobile,
} from './structure-template';

interface RoutesProps {
  path: string;
  element: JSX.Element;
  requireOrganization: boolean;
  children?: RoutesProps[];
  permissionScreen?: ActionRbacEnum;
  entitlementScreen?: EntitlementEnum;
}

const RoutesBalance = () => {
  const routes: RoutesProps[] = useMemo(
    () => [
      {
        path: ROUTES.ROOT,
        element: <HomeContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.DASHBOARD,
        element: <DashboardContainer />,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.LANDING_PAGE,
      },
      {
        path: ROUTES.AG_OPERATION,
        element: <AgOperationContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.ITEM,
        element: <ItemListContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.MACHINERY,
        element: <MachineryListContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.IMPORT,
        element: <ImportContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.ELECTRONIC_INVOICE_LINK,
        element: <ElectronicInvoiceLinkContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.AG_OPERATION_CREATE,
        element: <AgOperationCreateContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.MIGRATION,
        element: <MigrationContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.REPORTS,
        element: <ReportsContainer />,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.DATA_SHARING_IMPORT_EXPORT,
        children: [
          {
            path: ROUTES.REPORTS_START,
            element: <DataImportStartContainer />,
            requireOrganization: true,
          },
          {
            path: ROUTES.REPORTS_LIST,
            element: <DataImportListContainer />,
            requireOrganization: true,
          },
          {
            path: ROUTES.REPORTS_DETAILS,
            element: <DataImportDetailsContainer />,
            requireOrganization: true,
          },
        ],
      },
      {
        path: ROUTES.REPORT_GENERAL_LEDGER,
        element: <ReportGeneralLedger />,
        requireOrganization: true,
      },
      {
        path: ROUTES.REPORT_GENERAL_LEDGER,
        element: <ReportGeneralLedger />,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.REPORT_GENERAL_LEDGER,
      },
      {
        path: ROUTES.ITEM_CATEGORY,
        element: <ItemCategoryContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.BALANCE_AI_LIST,
        element: <BalanceAiListContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.NO_COST_RECORDS,
        element: <NoCostRecordsContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.PURCHASE,
        element: <PurchaseListContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.ACCOUNTS_PAYABLE,
        element: <AccountsPayableContainer />,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.ACCOUNTS_PLAYABLE,
      },
      {
        path: ROUTES.INVOICE,
        element: <InvoiceListContainer />,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.INVOICES,
      },
      {
        path: ROUTES.MATERIAL_CONSUMPTION,
        element: <MaterialConsumptionListContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.SUPPLIER,
        element: <SupplierList />,
        requireOrganization: true,
      },
      {
        path: ROUTES.BANK_ACCOUNT,
        element: <BankAccountListContainer />,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.INVOICES,
      },
      {
        path: ROUTES.HELP_CENTER,
        element: <HelpCenterContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.STOCK,
        element: <NewStockSummaryContainer />,
        permissionScreen: ActionRbacEnum.STOCK_BALANCE_READ,
        requireOrganization: true,
        children: [
          {
            path: ROUTES.MOVEMENTS_STOCK,
            element: <NewMovementsContainer />,
            requireOrganization: true,
          },
        ],
      },
      {
        path: ROUTES.COMMERCIALIZATION,
        element: <CommercializationContractsContainer />,
        permissionScreen: ActionRbacEnum.STOCK_BALANCE_READ,
        requireOrganization: true,
      },
      {
        path: ROUTES.COMMERCIALIZATION_STOCK,
        element: <CommercializationStockContainer />,
        permissionScreen: ActionRbacEnum.STOCK_BALANCE_READ,
        requireOrganization: true,
      },
      {
        path: ROUTES.COMMERCIALIZATION_MOVEMENTS,
        element: <CommercializationMovementsContainer />,
        permissionScreen: ActionRbacEnum.STOCK_BALANCE_READ,
        requireOrganization: true,
      },
      {
        path: ROUTES.FINANCIAL,
        element: <FinancialContainer />,
        permissionScreen: ActionRbacEnum.FINANCIAL_BALANCE_READ,
        requireOrganization: true,
      },
      {
        path: ROUTES.FINANCIAL_BUDGET,
        element: <FinancialBudgetContainer />,
        permissionScreen: ActionRbacEnum.FINANCIAL_BALANCE_READ,
        requireOrganization: true,
      },
      {
        path: ROUTES.SEASON_BUDGET_CREATE,
        element: <SeasonBudgetContainer />,
        permissionScreen: ActionRbacEnum.FINANCIAL_BALANCE_READ,
        requireOrganization: true,
      },
      {
        path: ROUTES.FINANCIAL_PRODUCTION,
        element: <FinancialProductionContainer />,
        permissionScreen: ActionRbacEnum.FINANCIAL_BALANCE_READ,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.PRODUCTION_YIELD_DATA,
      },
      {
        path: ROUTES.FINANCIAL_MACHINERY,
        element: <FinancialMachineryContainer />,
        permissionScreen: ActionRbacEnum.FINANCIAL_BALANCE_READ,
        requireOrganization: true,
        entitlementScreen: EntitlementEnum.PRODUCTION_YIELD_DATA,
      },
      {
        path: ROUTES.SETTINGS,
        element: <SettingsContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.ELECTRONIC_INVOICE,
        element: <ElectronicInvoiceContainer />,
        requireOrganization: true,
      },
      {
        path: ROUTES.NO_ORGANIZATION,
        element: <NoOrganization />,
        requireOrganization: false,
      },
      {
        path: ROUTES.MAINTENANCE,
        element: <MaintenanceContainer />,
        requireOrganization: false,
      },
      {
        path: ROUTES.NO_PERMISSION,
        element: <NoPermission />,
        requireOrganization: false,
      },
      {
        path: ROUTES.NO_PERMISSION_BALANCE,
        element: <NoPermissionBalance />,
        requireOrganization: false,
      },
    ],
    [],
  );

  return (
    <Routes>
      <Route element={<StructureTemplate />}>
        <Route
          path={ROUTES.AUTHENTICATE}
          element={
            <Suspense fallback={<Skeleton active />}>
              <AuthContainer />
            </Suspense>
          }
        />
        <Route element={<StructureTemplateMobile />}>
          <Route
            path={ROUTES.BALANCE_AI_TERMS_AND_CONDITIONS}
            element={
              <Suspense fallback={<Skeleton active />}>
                <BalanceAiTermsAndConditions />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ORGANIZATION_LEGACY}
            element={<Navigate to={ROUTES.MY_CROPWISE} replace />}
          />
          <Route
            path={ROUTES.FARM_LEGACY}
            element={<Navigate to={ROUTES.MY_CROPWISE} replace />}
          />
          <Route
            path={`${ROUTES.MY_CROPWISE}/*`}
            element={
              <AccessGuardian>
                <Suspense fallback={<Skeleton active />}>
                  <MyCropwiseContainer />
                </Suspense>
              </AccessGuardian>
            }
          />
          {routes.map((route) => (
            <Route path={route.path} key={route.path}>
              <Route
                index
                element={
                  <AccessGuardian>
                    <Suspense fallback={<Skeleton active />}>
                      {route.requireOrganization ? (
                        <RequireOrganization
                          permissionScreen={route.permissionScreen}
                          entitlementScreen={route.entitlementScreen}
                        >
                          {route.element}
                        </RequireOrganization>
                      ) : (
                        route.element
                      )}
                    </Suspense>
                  </AccessGuardian>
                }
              />
              {route.children?.map((routeChildren) => (
                <Route
                  key={routeChildren.path}
                  path={routeChildren.path}
                  element={
                    <AccessGuardian>
                      <Suspense fallback={<Skeleton active />}>
                        {routeChildren.requireOrganization ? (
                          <RequireOrganization
                            permissionScreen={routeChildren.permissionScreen}
                            entitlementScreen={routeChildren.entitlementScreen}
                          >
                            {routeChildren.element}
                          </RequireOrganization>
                        ) : (
                          routeChildren.element
                        )}
                      </Suspense>
                    </AccessGuardian>
                  }
                />
              ))}
            </Route>
          ))}
          <Route
            path="*"
            element={<Navigate to={ROUTES.DASHBOARD} replace />}
          />
        </Route>
      </Route>
    </Routes>
  );
};

export { RoutesBalance };
